import moment from "moment"

export interface IExcelExportColumn<Row = Record<string, unknown>> {
  key: string
  label: string
  type?: "string" | "number" | "date"
  value?: (row: Row, rowIndex: number) => unknown
}

export interface IExportStyledExcelOptions<Row = Record<string, unknown>> {
  filename: string
  sheetName: string
  title: string
  columns: IExcelExportColumn<Row>[]
  rows: Row[]
}

const TITLE_FILL = "FFF59E0B"
const HEADER_FILL = "FFD97706"
const ALT_ROW_FILL = "FFFFF7E6"
const BORDER_COLOR = "FFD9D9D9"
const INVALID_FILENAME_CHARS = new Set(["<", ">", ":", "\"", "/", "\\", "|", "?", "*"])

const sanitizeSheetName = (sheetName: string) => {
  const fallback = "Export"
  const safeName = (sheetName || fallback).replace(/[\\/*?:[\]]/g, " ").trim()
  return (safeName || fallback).slice(0, 31)
}

const sanitizeFilename = (filename: string) => {
  const fallback = "export.xlsx"
  const safeName = (filename || fallback)
    .split("")
    .map(char => (INVALID_FILENAME_CHARS.has(char) || char.charCodeAt(0) < 32 ? "_" : char))
    .join("")
    .trim()
  const normalized = safeName || fallback
  return normalized.toLowerCase().endsWith(".xlsx") ? normalized : `${normalized}.xlsx`
}

const normalizeNumber = (value: unknown) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "").trim()
    if (!cleaned) return null
    const parsed = Number(cleaned)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const normalizeDate = (value: unknown) => {
  if (!value) return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value
  }

  const parsed = moment(String(value), ["DD.MM.YYYY", "DD MMMM YYYY", "YYYY-MM-DD", moment.ISO_8601], true)
  return parsed.isValid() ? parsed.toDate() : null
}

const getCellValue = <Row extends Record<string, unknown>>(
  row: Row,
  rowIndex: number,
  column: IExcelExportColumn<Row>,
) => {
  const rawValue = column.value ? column.value(row, rowIndex) : row[column.key]

  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return ""
  }

  if (column.type === "number") {
    return normalizeNumber(rawValue) ?? String(rawValue)
  }

  if (column.type === "date") {
    return normalizeDate(rawValue) ?? String(rawValue)
  }

  if (typeof rawValue === "boolean") {
    return rawValue ? "Yes" : "No"
  }

  return rawValue
}

const toDisplayLength = (value: unknown) => {
  if (value === null || value === undefined) return 0
  if (value instanceof Date) return 18
  return String(value).length
}

const downloadBuffer = (buffer: ArrayBuffer, filename: string) => {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  })
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = sanitizeFilename(filename)
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.URL.revokeObjectURL(url)
}

export const exportStyledExcel = async <Row extends Record<string, unknown>>(
  options: IExportStyledExcelOptions<Row>,
) => {
  const { filename, sheetName, title, columns, rows } = options
  const activeColumns = columns.filter((column) => column.key && column.label)
  if (!activeColumns.length) {
    throw new Error("No columns available to export")
  }

  const ExcelJS = await import("exceljs")
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet(sanitizeSheetName(sheetName), {
    views: [{ state: "frozen", ySplit: 2 }],
  })

  workbook.creator = "FARMS Frontend"
  workbook.created = new Date()

  worksheet.addRow([title])
  const titleRow = worksheet.getRow(1)
  if (activeColumns.length > 1) {
    worksheet.mergeCells(1, 1, 1, activeColumns.length)
  }

  titleRow.height = 24
  const titleCell = worksheet.getCell(1, 1)
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: TITLE_FILL },
  }
  titleCell.font = {
    bold: true,
    color: { argb: "FFFFFFFF" },
    size: 14,
  }
  titleCell.alignment = {
    vertical: "middle",
    horizontal: "center",
  }

  worksheet.addRow(activeColumns.map((column) => column.label))
  const headerRow = worksheet.getRow(2)
  headerRow.height = 22
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: HEADER_FILL },
    }
    cell.font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
    }
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    }
    cell.border = {
      top: { style: "thin", color: { argb: BORDER_COLOR } },
      left: { style: "thin", color: { argb: BORDER_COLOR } },
      bottom: { style: "thin", color: { argb: BORDER_COLOR } },
      right: { style: "thin", color: { argb: BORDER_COLOR } },
    }
  })

  rows.forEach((row, rowIndex) => {
    const values = activeColumns.map((column) => getCellValue(row, rowIndex, column))
    const excelRow = worksheet.addRow(values)
    const isAltRow = rowIndex % 2 === 1

    excelRow.eachCell((cell, cellIndex) => {
      cell.alignment = {
        vertical: "top",
        horizontal: typeof cell.value === "number" ? "right" : "left",
        wrapText: true,
      }
      cell.border = {
        top: { style: "thin", color: { argb: BORDER_COLOR } },
        left: { style: "thin", color: { argb: BORDER_COLOR } },
        bottom: { style: "thin", color: { argb: BORDER_COLOR } },
        right: { style: "thin", color: { argb: BORDER_COLOR } },
      }
      if (isAltRow) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: ALT_ROW_FILL },
        }
      }

      const columnType = activeColumns[cellIndex - 1]?.type
      if (columnType === "date" && cell.value instanceof Date) {
        cell.numFmt = "dd mmmm yyyy"
      }
      if (columnType === "number" && typeof cell.value === "number") {
        cell.numFmt = Number.isInteger(cell.value) ? "#,##0" : "#,##0.00"
      }
    })
  })

  worksheet.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: activeColumns.length },
  }

  activeColumns.forEach((column, columnIndex) => {
    const columnValues = rows.map((row, rowIndex) => getCellValue(row, rowIndex, column))
    const longestContent = Math.max(
      column.label.length,
      ...columnValues.map((value) => toDisplayLength(value)),
    )
    const preferredWidth =
      column.type === "date"
        ? 18
        : column.type === "number"
          ? Math.max(14, Math.min(18, longestContent + 2))
          : Math.max(14, Math.min(36, longestContent + 2))

    worksheet.getColumn(columnIndex + 1).width = preferredWidth
  })

  const buffer = await workbook.xlsx.writeBuffer()
  downloadBuffer(buffer as ArrayBuffer, filename)
}
